import {
  type StatisticsDto,
  type StatisticsMetric,
  type StatisticsQuery,
  statisticsDateSchema,
  validateStatisticsSearch,
} from "@repo/polaris-shared";

/** The tab lives in the URL next to the filters, but never reaches the API. */
export type StatisticsSearch = StatisticsQuery & { view?: "money" };
export function validateStatisticsPageSearch(raw: Record<string, unknown>): StatisticsSearch {
  return { ...validateStatisticsSearch(raw), ...(raw.view === "money" ? { view: "money" } : {}) };
}

const DAY = 86_400_000;
export const shiftDay = (day: string, days: number) =>
  new Date(Date.parse(`${day}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);
export const dayCount = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY) + 1;
export function mondayOf(day: string): string {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return shiftDay(day, -((weekday + 6) % 7));
}
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export type StatisticsPeriodChoice = "week" | "month" | "last-month" | "quarter" | "all" | "custom";
/** Calendar shortcuts the API knows only as custom dates. */
export function statisticsShortcuts(today: string) {
  const [year = 0, month = 1, day = 1] = today.split("-").map(Number);
  const previousYear = month === 1 ? year - 1 : year;
  const previousMonth = month === 1 ? 12 : month - 1;
  const lastMonth = `${previousYear}-${String(previousMonth).padStart(2, "0")}`;
  const quarterMonth = ((month - 4 + 12) % 12) + 1;
  const quarterYear = month <= 3 ? year - 1 : year;
  const quarterDay = Math.min(day, lastDayOfMonth(quarterYear, quarterMonth));
  const quarterStart = `${quarterYear}-${String(quarterMonth).padStart(2, "0")}-${String(quarterDay).padStart(2, "0")}`;
  return {
    "last-month": {
      from: `${lastMonth}-01`,
      to: `${lastMonth}-${String(lastDayOfMonth(previousYear, previousMonth)).padStart(2, "0")}`,
    },
    quarter: { from: shiftDay(quarterStart, 1), to: today },
  };
}
export function statisticsPeriodChoice(
  filters: StatisticsQuery,
  today: string,
): StatisticsPeriodChoice {
  if (filters.preset !== "custom") return filters.preset;
  const shortcuts = statisticsShortcuts(today);
  for (const choice of ["last-month", "quarter"] as const)
    if (shortcuts[choice].from === filters.from && shortcuts[choice].to === filters.to)
      return choice;
  return "custom";
}

/**
 * The weekly columns read at least twelve weeks from a Monday, so a short period is shown among
 * the weeks before it, up to the Sunday that closes the period: a week cut by the period's end
 * would look like a drop. Only the running week stays partial. Massimo already covers everything.
 */
export function statisticsChartQuery(
  filters: StatisticsQuery,
  today: string,
): StatisticsQuery | null {
  if (filters.preset === "all") return null;
  const end = filters.preset === "custom" && filters.to ? filters.to : today;
  const sunday = shiftDay(mondayOf(end), 6);
  const to = sunday < today ? sunday : today;
  const selectedFrom =
    filters.preset === "custom" && filters.from
      ? filters.from
      : filters.preset === "week"
        ? mondayOf(today)
        : `${today.slice(0, 7)}-01`;
  const twelveWeeks = shiftDay(mondayOf(to), -77);
  const from = mondayOf(selectedFrom) < twelveWeeks ? mondayOf(selectedFrom) : twelveWeeks;
  return {
    preset: "custom",
    from,
    to,
    ...(filters.operatorId ? { operatorId: filters.operatorId } : {}),
    ...(filters.source ? { source: filters.source } : {}),
  };
}

const percentFormat = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 1 });
const countFormat = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 });

export type StatisticsDirection = "up" | "down";
export type StatisticsChange = {
  label: string;
  tone: "better" | "worse" | "same";
  trend: "up" | "down" | "same";
  partial: boolean;
};
/** Percent metrics change in points, counts in percent, durations in their own unit. */
export function statisticsChange(
  current: StatisticsMetric,
  previous: StatisticsMetric | undefined,
  kind: "count" | "percent" | "minutes" | "money",
  better: StatisticsDirection = "up",
  formatAmount: (value: number) => string = (value) => String(value),
): StatisticsChange | null {
  if (!previous || current.value === null || previous.value === null) return null;
  const raw = current.value - previous.value;
  // What the label prints: a change that rounds to nothing («−0 punti», «+0%») is no change.
  const printed = kind === "count" && previous.value !== 0 ? (raw / previous.value) * 100 : raw;
  const difference = Number(printed.toFixed(1)) === 0 ? 0 : raw;
  const trend = difference === 0 ? "same" : difference > 0 ? "up" : "down";
  const tone = trend === "same" ? "same" : trend === better ? "better" : "worse";
  const sign = difference > 0 ? "+" : difference < 0 ? "−" : "";
  const partial = Boolean(current.reason || previous.reason);
  if (trend === "same") return { label: "Invariato", tone, trend, partial };
  if (kind === "percent") {
    const points = percentFormat.format(Math.abs(difference));
    return {
      label: `${sign}${points} ${points === "1" ? "punto" : "punti"}`,
      tone,
      trend,
      partial,
    };
  }
  if (kind === "minutes" || kind === "money")
    return { label: `${sign}${formatAmount(Math.abs(difference))}`, tone, trend, partial };
  if (previous.value === 0)
    return { label: `${sign}${countFormat.format(Math.abs(difference))}`, tone, trend, partial };
  return {
    label: `${sign}${percentFormat.format(Math.abs((difference / previous.value) * 100))}%`,
    tone,
    trend,
    partial,
  };
}

/** Only spend is combined across accounts; CPL definitions may differ between them. */
export function statisticsSpendTotals(accounts: StatisticsDto["advertising"]) {
  const totals = new Map<
    string,
    { currency: string; amount: number | null; reason: string | null; accounts: number }
  >();
  for (const account of accounts) {
    const previous = totals.get(account.currency);
    const amount =
      previous?.amount === null || account.spend.value === null
        ? null
        : (previous?.amount ?? 0) + account.spend.value;
    totals.set(account.currency, {
      currency: account.currency,
      amount,
      reason: previous?.reason ?? account.spend.reason,
      accounts: (previous?.accounts ?? 0) + 1,
    });
  }
  return [...totals.values()];
}

export function statisticsRangeError(from: string, to: string, today: string): string | null {
  if (!from || !to) return "Scegli entrambe le date.";
  if (!statisticsDateSchema.safeParse(from).success || !statisticsDateSchema.safeParse(to).success)
    return "Scegli date valide.";
  if (from > to) return "La data finale precede quella iniziale.";
  if (from > today || to > today) return "Scegli date fino a oggi.";
  return null;
}

export type StatisticsTrendBucket = {
  from: string;
  to: string;
  leads: number;
  closures: number;
};

/** Calendar grouping preserves every result, including sparse dates and DST boundaries. */
export function statisticsTrend(
  trend: StatisticsDto["trend"],
  serverGranularity?: "day" | "week" | "month",
  range?: Pick<StatisticsDto["range"], "from" | "to">,
): {
  granularity: "Giorni" | "Settimane" | "Mesi";
  buckets: StatisticsTrendBucket[];
} {
  const sorted = [...trend].sort((left, right) => left.date.localeCompare(right.date));
  if (serverGranularity) {
    return {
      granularity:
        serverGranularity === "day"
          ? "Giorni"
          : serverGranularity === "week"
            ? "Settimane"
            : "Mesi",
      buckets: sorted.map((row) => {
        const end = new Date(`${row.date}T00:00:00Z`);
        if (serverGranularity === "week") end.setUTCDate(end.getUTCDate() + 6);
        if (serverGranularity === "month") end.setUTCMonth(end.getUTCMonth() + 1, 0);
        const through = end.toISOString().slice(0, 10);
        return {
          from: range && row.date < range.from ? range.from : row.date,
          to: range && through > range.to ? range.to : through,
          leads: row.leads,
          closures: row.closures,
        };
      }),
    };
  }
  const first = sorted[0]?.date;
  const last = sorted[sorted.length - 1]?.date;
  const days = first && last ? (Date.parse(last) - Date.parse(first)) / 86_400_000 : 0;
  const granularity = days <= 62 ? "Giorni" : days <= 365 ? "Settimane" : "Mesi";
  const groups = new Map<string, StatisticsTrendBucket>();
  for (const day of sorted) {
    let key = day.date;
    if (granularity === "Mesi") key = `${day.date.slice(0, 7)}-01`;
    if (granularity === "Settimane") {
      const date = new Date(`${day.date}T00:00:00Z`);
      date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
      key = date.toISOString().slice(0, 10);
    }
    const current = groups.get(key);
    if (current) {
      current.to = day.date;
      current.leads += day.leads;
      current.closures += day.closures;
    } else {
      groups.set(key, { from: day.date, to: day.date, leads: day.leads, closures: day.closures });
    }
  }
  return { granularity, buckets: [...groups.values()] };
}
